/**
 * ElementRenderingEngine
 *
 * Pure SVG element creation engine.
 * Separates rendering logic from calculation and positioning.
 *
 * Architecture: Universal Layer-Based Architecture v2.0.0
 * Phase: Rendering Layer Implementation
 *
 * Features:
 * - Pure SVG element creation
 * - Mode-independent rendering functions
 * - Comprehensive debug traces
 * - Memory-efficient DOM operations
 * - Overlay layer management
 *
 * Delegates to extracted modules:
 * - SpriteRenderingModule: sprite creation, deferred sprite queue, D3PlanetSpriteManager
 * - CollisionDetectionModule: perpendicular position calculation, collision checks
 * - RenderingEventHandler: event subscription setup and cleanup
 */

import * as d3 from 'd3';
import { devLog, devWarn, devError } from '../../../../../utils/devLog';
import { PositionedElement } from './ElementCalculationEngine';
import {
    DEGREE_DIVISIONS_CONFIG
} from '../config/PlanetLayerConfig';
import { chartColorPresetManager } from '../../../../../config/chartColorPresets';
import { FONTS } from '../../../../../config/fonts';
import { globalEventStream, createEvent } from '../core/EventStream';
import type {
    D3PlanetSpriteManager,
} from '../animation';
import { SECONDARY_DATA_SPRITE_CONFIG } from '../config/PlanetLayerConfig';
import { SharedDataContext } from '../core/SharedDataContext';
import { ElementPreparationEngine } from './ElementPreparationEngine';
import { EngineStatsTracker, DebugTraceManager } from './shared/EngineStatsTracker';

// Extracted modules
import {
    calculatePerpendicularPosition as calcPerpPosition,
    POSITION_CONSTANTS,
    type PlanetCacheEntry,
    type PerpendicularPositionConfig,
    type GeometryCallbacks
} from './rendering/CollisionDetectionModule';
import { RenderingEventHandler, type RenderingEventCallbacks } from './rendering/RenderingEventHandler';
import { SpriteRenderingModule, type SpriteGeometryCallbacks } from './rendering/SpriteRenderingModule';

// Debug trace configuration
const DEBUG_TRACES = DebugTraceManager.create({
    RENDERING_ENGINE: false,
    SVG_CREATION: false,
    ELEMENT_STYLING: false,
    COLLISION_DETECTION: false,
    DOM_OPERATIONS: false,
    OVERLAY_MANAGEMENT: false,
    PERFORMANCE_TRACKING: false
});

/**
 * Rendering configuration for different element types
 */
export interface RenderingConfig {
    chartId?: string; // Chart identifier for multi-chart support
    mode: 'symbols' | 'short' | 'full';
    showDegreelines: boolean;
    showYogaGroups: boolean;
    showPlanets?: boolean;
    enableAnimations: boolean;
    debugMode: boolean;
    customStyles?: Record<string, any>;
    horaCurrentPlanet?: string; // Current hora planet from API
}

/**
 * SVG element with metadata
 */
export interface RenderedElement {
    id: string;
    element: SVGElement;
    type: 'planet' | 'yoga' | 'line' | 'text' | 'sprite';
    layer: 'container' | 'lines-overlay' | 'planets-overlay' | 'degrees-line';
    debugInfo: {
        renderTime: number;
        elementCount: number;
        memoryEstimate: number;
    };
}

/**
 * Render result with performance data
 */
export interface RenderResult {
    elements: RenderedElement[];
    totalElements: number;
    renderTime: number;
    memoryUsage: number;
    layerDistribution: Record<string, number>;
    debugTrace?: string[];
}

/**
 * Style configuration for different element types
 */
export interface ElementStyleConfig {
    planet: {
        symbolSize: number;
        fontSize: number;
        color: string;
        strokeWidth: number;
    };
    yoga: {
        circleRadius: number;
        strokeWidth: number;
        fillOpacity: number;
        textSize: number;
    };
    degreeLine: {
        strokeWidth: number;
        color: string;
        opacity: number;
        dashArray?: string;
    };
    text: {
        fontSize: number;
        fontFamily: string;
        fill: string;
        textAnchor: string;
    };
}


/**
 * Pure SVG rendering engine
 */
export class ElementRenderingEngine {
    private static instance: ElementRenderingEngine | null = null;

    // Extracted modules
    private spriteModule: SpriteRenderingModule;
    private eventHandler: RenderingEventHandler;

    // Planet collision avoidance cache (shared with modules)
    private globalPlanetCache: PlanetCacheEntry[] = [];

    // Performance tracking
    private statsTracker = new EngineStatsTracker(
        { totalRenders: 0, totalElements: 0, averageRenderTime: 0, memoryUsage: 0 },
        'totalRenders',
        'averageRenderTime'
    );

    // Default style configurations
    private defaultStyles: ElementStyleConfig = {
        planet: {
            symbolSize: 16,
            fontSize: 12,
            color: chartColorPresetManager.getCurrentPreset().chartElements.planetColor,
            strokeWidth: 1
        },
        yoga: {
            circleRadius: 14,
            strokeWidth: 2,
            fillOpacity: 0.8,
            textSize: 10
        },
        degreeLine: {
            strokeWidth: 1,
            color: chartColorPresetManager.getCurrentPreset().degreeLines.mainStroke,
            opacity: 0.7
        },
        text: {
            fontSize: 12,
            fontFamily: FONTS.chart.primary,
            fill: chartColorPresetManager.getCurrentPreset().chartElements.textFill,
            textAnchor: 'middle'
        }
    };

    private constructor() {
        // Create geometry callbacks for sprite module
        const geometryCallbacks: SpriteGeometryCallbacks = {
            calculateHousePositionForDegreeLines: (house) => this.calculateHousePositionForDegreeLines(house),
            calculatePlanetPositionOld: (ci, pd, cb) => this.calculatePlanetPositionOld(ci, pd, cb),
            calculatePerpendicularPosition: (config) => this.calculatePerpendicularPosition(config),
            calculateConnectionLineRotation: (ci, td, cb, lp, pp) => this.calculateConnectionLineRotation(ci, td, cb, lp, pp),
        };

        // Initialize extracted modules
        this.spriteModule = new SpriteRenderingModule(geometryCallbacks);

        const eventCallbacks: RenderingEventCallbacks = {
            initializeD3SpriteSystem: () => this.spriteModule.initializeD3SpriteSystem(),
            initializeSecondaryDataSpriteSystem: () => this.spriteModule.initializeSecondaryDataSpriteSystem(),
            processDeferredSprites: () => this.spriteModule.processDeferredSprites(this.globalPlanetCache),
            handleChartDataClear: (event, chartId) => this.handleChartDataClear(event, chartId),
            handleSecondaryDataPositioning: (event) => this.handleSecondaryDataPositioning(event),
            updatePlanetPositionsFromDrag: (allPlanets, targetSpriteManager) =>
                this.spriteModule.updatePlanetPositionsFromDrag(allPlanets, this.globalPlanetCache, targetSpriteManager),
            getSpriteManagerForChart: (chartId) => this.spriteModule.getSpriteManagerForChart(chartId),
        };

        this.eventHandler = new RenderingEventHandler(eventCallbacks);
        this.eventHandler.setupEventSubscriptions();
    }

    public static getInstance(): ElementRenderingEngine {
        if (!ElementRenderingEngine.instance) {
            ElementRenderingEngine.instance = new ElementRenderingEngine();
        }
        return ElementRenderingEngine.instance;
    }

    /**
     * Set current chart ID for display mode settings
     */
    public setChartId(chartId?: string): void {
        this.spriteModule.setChartId(chartId);
    }

    // --- Public API: deferredSpriteData (kept for backward compatibility) ---

    public get deferredSpriteData() {
        return this.spriteModule.deferredSpriteData;
    }

    public set deferredSpriteData(value: any[]) {
        this.spriteModule.deferredSpriteData = value;
    }

    // --- Collision detection delegation ---

    /**
     * Unified perpendicular position calculation (delegates to module)
     */
    private calculatePerpendicularPosition(config: PerpendicularPositionConfig): { x: number, y: number } | null {
        const geometry: GeometryCallbacks = {
            calculateArcTangent: (ci, p, cb, ir) => this.calculateArcTangent(ci, p, cb, ir),
            calculateDegreeLineCoordinatesOld: (ci, cb, ir) => this.calculateDegreeLineCoordinatesOld(ci, cb, ir),
        };
        return calcPerpPosition(config, this.globalPlanetCache, geometry);
    }

    // --- Chart clear handling ---

    private handleChartDataClear(event: any, chartId?: string): void {
        devLog(`[ElementRenderingEngine] handleChartDataClear called for chartId: ${chartId || 'all'}`);
        this.spriteModule.handleChartDataClear(event, chartId);
        // Clear planet position cache for collision detection
        this.globalPlanetCache = [];
    }

    /**
     * Clear sprites before re-rendering (called from TabChartRenderer)
     */
    public clearSpritesForRerender(): void {
        devLog('[ElementRenderingEngine] clearSpritesForRerender called');
        this.spriteModule.clearSpritesForRerender();
        this.globalPlanetCache = [];
    }

    /**
     * Clear only planet elements for animation updates (selective clearing)
     */
    private clearAnimationElements(): void {
        try {
            const allOverlays = d3.selectAll('.lines-overlay, .planets-overlay');

            if (!allOverlays.empty()) {
                allOverlays.selectAll('.planet-circle-on-line').remove();
                allOverlays.selectAll('.planet-text-on-line').remove();
                allOverlays.selectAll('[data-element-id^="planet-circle-house-"]').remove();
                allOverlays.selectAll('[data-element-id^="planet-connection-house-"]').remove();
                allOverlays.selectAll('[data-element-id^="planet-text-house-"]').remove();
                allOverlays.selectAll('.planet-element').remove();
            }

        } catch (error) {
            devError('ElementRenderingEngine: Error clearing animation elements:', error);
        }
    }

    /**
     * Render elements to specified SVG containers
     */
    public renderElements(
        elements: PositionedElement[],
        containerLayer: d3.Selection<SVGGElement, unknown, any, any>,
        linesOverlay?: d3.Selection<SVGGElement, unknown, any, any>,
        planetsOverlay?: d3.Selection<SVGGElement, unknown, any, any>,
        config: RenderingConfig = { mode: 'short', showDegreelines: true, showYogaGroups: true, enableAnimations: false, debugMode: true },
        eventSource?: string
    ): RenderResult {
        const startTime = performance.now();

        // Clear old planet elements if this is an animation update
        if (eventSource === 'animation-update') {
            this.clearAnimationElements();
        }

        const renderedElements: RenderedElement[] = [];
        const layerDistribution: Record<string, number> = {
            container: 0,
            'lines-overlay': 0,
            'planets-overlay': 0,
            'degrees-line': 0
        };

        // Group elements by type for efficient rendering
        const elementGroups = this.groupElementsByType(elements);

        // Render degree lines first (if enabled)
        if (config.showDegreelines && linesOverlay) {
            const lineElements = this.renderDegreeLines(elementGroups, linesOverlay, config);
            renderedElements.push(...lineElements);

            lineElements.forEach(element => {
                layerDistribution[element.layer] = (layerDistribution[element.layer] || 0) + 1;
            });
        }

        // Planet rendering via sprites - ACTIVE sprite creation
        const planetElements = elements.filter(element => element.type === 'planet');

        if (planetElements.length > 0) {
            const spriteManager = this.spriteModule.getSpriteManager();
            if (spriteManager && config.showPlanets !== false) {
                planetElements.forEach(planetElement => {
                    try {
                        spriteManager.createPlanetSprite(
                            planetElement.displayName,
                            planetElement.containerIndex,
                            {
                                x: planetElement.coordinates.x,
                                y: planetElement.coordinates.y
                            },
                            {},
                            {
                                name: planetElement.name,
                                shortName: planetElement.displayName
                            },
                            config.horaCurrentPlanet
                        );
                    } catch (_error) {
                    }
                });

                layerDistribution['planet-sprites'] = planetElements.length;
            } else {
                layerDistribution['planet-elements-pending'] = planetElements.length;
            }
        }

        // Calculate performance metrics
        const renderTime = performance.now() - startTime;
        const memoryUsage = this.estimateMemoryUsage(renderedElements);

        this.updateRenderStats(renderTime, renderedElements.length, memoryUsage);

        const result: RenderResult = {
            elements: renderedElements,
            totalElements: renderedElements.length,
            renderTime,
            memoryUsage,
            layerDistribution,
            debugTrace: config.debugMode ? this.generateDebugTrace(elements, renderedElements, renderTime) : undefined
        };

        return result;
    }

    /**
     * Update planet positions from Motion Buffer API data using D3 sprite animations
     */
    public updatePlanetPositionsFromMotionBuffer(
        motionData: any,
        enableAnimations: boolean = true,
        animationDuration: number = 200
    ): Promise<void> {
        return this.spriteModule.updatePlanetPositionsFromMotionBuffer(
            motionData, enableAnimations, animationDuration, this.globalPlanetCache
        );
    }

    /**
     * Enable or disable sprite animations
     */
    public setSpriteAnimationEnabled(enabled: boolean): void {
        this.spriteModule.setSpriteAnimationEnabled(enabled);
    }

    /**
     * Get sprite system statistics for debugging
     */
    public getSpriteSystemStats(): any {
        const stats = this.spriteModule.getSpriteSystemStats();
        if (stats.enabled !== false) {
            stats.performance = this.statsTracker.get();
        }
        return stats;
    }

    /**
     * Setup D3 Sprite Manager when SVG is available (public for external callers)
     */
    public setupD3SpriteManager(): void {
        this.spriteModule.setupD3SpriteManager();
    }

    // --- Element grouping ---

    private groupElementsByType(elements: PositionedElement[]): {
        planets: PositionedElement[];
        yogas: PositionedElement[];
        degreeLines: PositionedElement[];
    } {
        const groups = {
            planets: [] as PositionedElement[],
            yogas: [] as PositionedElement[],
            degreeLines: [] as PositionedElement[]
        };

        for (const element of elements) {
            if (element.type === 'yoga') {
                groups.yogas.push(element);
            } else {
                groups.planets.push(element);
            }

            if (element.coordinates.lineCoords) {
                groups.degreeLines.push(element);
            }
        }

        return groups;
    }

    // --- Degree line rendering ---

    private renderDegreeLines(
        elementGroups: { degreeLines: PositionedElement[] },
        linesOverlay: d3.Selection<SVGGElement, unknown, any, any>,
        config: RenderingConfig
    ): RenderedElement[] {
        const startTime = performance.now();
        const renderedElements: RenderedElement[] = [];

        let degreesLineLayer = linesOverlay.select('.degrees-line') as any;
        if (degreesLineLayer.empty()) {
            degreesLineLayer = linesOverlay.append('g').attr('class', 'degrees-line') as any;
        }

        const existingLines = degreesLineLayer.selectAll('.degree-line');
        const needsLineCreation = existingLines.empty();

        for (let containerIndex = 0; containerIndex < 12; containerIndex++) {
            if (needsLineCreation) {
                const lineElement = this.createHouseDegreeLineOld(containerIndex, config);
                if (lineElement) {
                    degreesLineLayer.node()?.appendChild(lineElement);

                    renderedElements.push({
                        id: `degree-line-container-${containerIndex}`,
                        element: lineElement,
                        type: 'line',
                        layer: 'degrees-line',
                        debugInfo: {
                            renderTime: performance.now() - startTime,
                            elementCount: 1,
                            memoryEstimate: this.estimateElementMemory(lineElement)
                        }
                    });

                    const divisionElements = this.renderDegreeDivisions(containerIndex, degreesLineLayer, config);
                    renderedElements.push(...divisionElements);
                }
            }

            // Defer planet sprite creation (they render after lines.ready)
            this.spriteModule.deferSpriteCreation(containerIndex, elementGroups.degreeLines, config);
        }

        const duration = performance.now() - startTime;

        // Emit lines.ready event after degree lines are completely rendered
        const linesReadyEvent = createEvent(
            'lines.ready',
            'element-rendering-engine',
            {
                metadata: {
                    chartId: config.chartId,
                },
                elementsCount: renderedElements.length,
                renderTime: duration,
                linesOverlayReady: true
            }
        );
        globalEventStream.emit(linesReadyEvent);

        return renderedElements;
    }

    // --- House degree line creation ---

    private createHouseDegreeLineOld(containerIndex: number, config: RenderingConfig): SVGElement | null {
        try {
            const housePosition = this.calculateHousePositionForDegreeLines(containerIndex);

            const relativeBounds = {
                width: housePosition.width,
                height: housePosition.height,
                x: 0,
                y: 0
            };

            const lineCoords = this.calculateDegreeLineCoordinatesOld(containerIndex, relativeBounds, 0.5);

            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

            const isCornerHouse = [0, 3, 6, 9].includes(containerIndex);
            let pathData: string;

            if (isCornerHouse) {
                pathData = this.createCornerArcPathOld(containerIndex, lineCoords, relativeBounds, 0.5);
                pathData = this.adjustPathToAbsolutePosition(pathData, housePosition.x, housePosition.y);
            } else {
                const x1 = lineCoords.startX + housePosition.x;
                const y1 = lineCoords.startY + housePosition.y;
                const x2 = lineCoords.endX + housePosition.x;
                const y2 = lineCoords.endY + housePosition.y;
                pathData = `M ${x1} ${y1} L ${x2} ${y2}`;
            }

            path.setAttribute('d', pathData);
            const currentPreset = chartColorPresetManager.getCurrentPreset();
            path.setAttribute('stroke', currentPreset.degreeLines.mainStroke);
            path.setAttribute('stroke-width', currentPreset.degreeLines.mainWidth.toString());
            path.setAttribute('stroke-linecap', 'none');
            path.setAttribute('fill', 'none');
            path.setAttribute('class', isCornerHouse ? 'degree-line corner-arc' : 'degree-line edge-line');
            path.setAttribute('data-house', containerIndex.toString());

            return path;

        } catch (error) {
            devError(`Failed to create degree line for container ${containerIndex}:`, error);
            return null;
        }
    }

    // --- House position calculation ---

    private calculateHousePositionForDegreeLines(house: number): { x: number, y: number, width: number, height: number } {
        const viewportSize = 700;
        const chartSize = 690;
        const cellSize = chartSize / 4;
        const offsetX = (viewportSize - chartSize) / 2;
        const offsetY = (viewportSize - chartSize) / 2;

        const positions = [
            { x: 0, y: 0 },
            { x: cellSize, y: 0 },
            { x: cellSize * 2, y: 0 },
            { x: cellSize * 3, y: 0 },
            { x: cellSize * 3, y: cellSize },
            { x: cellSize * 3, y: cellSize * 2 },
            { x: cellSize * 3, y: cellSize * 3 },
            { x: cellSize * 2, y: cellSize * 3 },
            { x: cellSize, y: cellSize * 3 },
            { x: 0, y: cellSize * 3 },
            { x: 0, y: cellSize * 2 },
            { x: 0, y: cellSize }
        ];

        const pos = positions[house] || { x: 0, y: 0 };

        return {
            x: pos.x + offsetX,
            y: pos.y + offsetY,
            width: cellSize,
            height: cellSize
        };
    }

    public getContainerBounds(containerIndex: number): { width: number, height: number, x: number, y: number } {
        const housePosition = this.calculateHousePositionForDegreeLines(containerIndex);
        return {
            width: housePosition.width,
            height: housePosition.height,
            x: housePosition.x,
            y: housePosition.y
        };
    }

    public calculatePlanetPositionForAnimation(containerIndex: number, degreesInHouse: number): { x: number, y: number } | null {
        const housePosition = this.calculateHousePositionForDegreeLines(containerIndex);
        const containerBounds = {
            width: housePosition.width,
            height: housePosition.height,
            x: 0,
            y: 0
        };

        const linePosition = this.calculatePlanetPositionOld(containerIndex, degreesInHouse, containerBounds);
        if (!linePosition) {
            return null;
        }

        linePosition.x += housePosition.x;
        linePosition.y += housePosition.y;

        return linePosition;
    }

    public calculatePerpendicularPositionForAnimation(
        containerIndex: number,
        linePosition: { x: number, y: number },
        degreesInHouse: number,
        positionIndex: number = 0
    ): { x: number, y: number } | null {
        const housePosition = this.calculateHousePositionForDegreeLines(containerIndex);
        const containerBounds = {
            width: housePosition.width,
            height: housePosition.height,
            x: 0,
            y: 0
        };

        const localLinePosition = {
            x: linePosition.x - housePosition.x,
            y: linePosition.y - housePosition.y
        };

        const perpendicularPosition = this.calculatePerpendicularPosition({
            containerIndex,
            containerBounds,
            linePosition: localLinePosition,
            yogaDegree: degreesInHouse,
            positionIndex,
            useCollisionDetection: false
        });
        if (!perpendicularPosition) {
            return null;
        }

        perpendicularPosition.x += housePosition.x;
        perpendicularPosition.y += housePosition.y;

        return perpendicularPosition;
    }

    // --- Path coordinate adjustments ---

    private adjustPathToAbsolutePosition(pathData: string, xOffset: number, yOffset: number): string {
        let result = pathData.replace(/([ML])\s*(-?\d+(?:\.\d+)?)\s*(-?\d+(?:\.\d+)?)/g, (_match, command, x, y) => {
            const newX = parseFloat(x) + xOffset;
            const newY = parseFloat(y) + yOffset;
            return `${command} ${newX} ${newY}`;
        });

        result = result.replace(/A\s*(-?\d+(?:\.\d+)?)\s*(-?\d+(?:\.\d+)?)\s*(-?\d+(?:\.\d+)?)\s*(-?\d+(?:\.\d+)?)\s*(-?\d+(?:\.\d+)?)\s*(-?\d+(?:\.\d+)?)\s*(-?\d+(?:\.\d+)?)/g,
            (_match, rx, ry, rotation, largeArc, sweep, x, y) => {
                const newX = parseFloat(x) + xOffset;
                const newY = parseFloat(y) + yOffset;
                return `A ${rx} ${ry} ${rotation} ${largeArc} ${sweep} ${newX} ${newY}`;
            });

        return result;
    }

    // --- Degree line coordinate calculations ---

    private calculateDegreeLineCoordinatesOld(containerIndex: number, containerBounds: any, insetRatio: number = 0): any {
        let result: any;
        const insetOffset = containerBounds.width * insetRatio;

        switch(containerIndex) {
            case 0: {
                const start = { x: insetOffset, y: containerBounds.height };
                const end = { x: containerBounds.width, y: insetOffset };
                result = { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
                break;
            }
            case 1: {
                const yPos = insetOffset;
                result = { startX: 0, startY: yPos, endX: containerBounds.width, endY: yPos };
                break;
            }
            case 2: {
                const yPos = insetOffset;
                result = { startX: 0, startY: yPos, endX: containerBounds.width, endY: yPos };
                break;
            }
            case 3: {
                const start = { x: 0, y: insetOffset };
                const end = { x: containerBounds.width - insetOffset, y: containerBounds.height };
                result = { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
                break;
            }
            case 4: {
                const xPos = containerBounds.width - insetOffset;
                result = { startX: xPos, startY: 0, endX: xPos, endY: containerBounds.height };
                break;
            }
            case 5: {
                const xPos = containerBounds.width - insetOffset;
                result = { startX: xPos, startY: 0, endX: xPos, endY: containerBounds.height };
                break;
            }
            case 6: {
                const start = { x: containerBounds.width - insetOffset, y: 0 };
                const end = { x: 0, y: containerBounds.height - insetOffset };
                result = { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
                break;
            }
            case 7: {
                const yPos = containerBounds.height - insetOffset;
                result = { startX: containerBounds.width, startY: yPos, endX: 0, endY: yPos };
                break;
            }
            case 8: {
                const yPos = containerBounds.height - insetOffset;
                result = { startX: containerBounds.width, startY: yPos, endX: 0, endY: yPos };
                break;
            }
            case 9: {
                const start = { x: containerBounds.width, y: containerBounds.height - insetOffset };
                const end = { x: insetOffset, y: 0 };
                result = { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
                break;
            }
            case 10: {
                const xPos = insetOffset;
                result = { startX: xPos, startY: containerBounds.height, endX: xPos, endY: 0 };
                break;
            }
            case 11: {
                const xPos = insetOffset;
                result = { startX: xPos, startY: containerBounds.height, endX: xPos, endY: 0 };
                break;
            }
            default: {
                result = { startX: 0, startY: containerBounds.height / 2, endX: containerBounds.width, endY: containerBounds.height / 2 };
                break;
            }
        }

        return result;
    }

    // --- Degree division rendering ---

    private renderDegreeDivisions(containerIndex: number, linesOverlay: d3.Selection<SVGGElement, unknown, any, any>, config: RenderingConfig): RenderedElement[] {
        const renderedElements: RenderedElement[] = [];
        const startTime = performance.now();

        const localConfig = {
            ...DEGREE_DIVISIONS_CONFIG,
            DIVISION_LENGTH: 5,
            MAJOR_DIVISION_LENGTH: 13,
            PERPENDICULAR_OFFSET: 3,
            ARC_DIVISION_ENABLED: true
        };

        if (!localConfig.ENABLED) return renderedElements;

        const housePosition = this.calculateHousePositionForDegreeLines(containerIndex);
        const relativeBounds = {
            width: housePosition.width,
            height: housePosition.height,
            x: 0,
            y: 0
        };

        const isCornerHouse = [0, 3, 6, 9].includes(containerIndex);
        const totalDivisions = 30 / DEGREE_DIVISIONS_CONFIG.DIVISION_SPACING;

        for (let i = 1; i <= totalDivisions; i++) {
            const degree = i * DEGREE_DIVISIONS_CONFIG.DIVISION_SPACING;
            const progress = degree / 30;
            const isMajor = degree % 5 === 0;

            let divisionMark: SVGElement;

            if (isCornerHouse && localConfig.ARC_DIVISION_ENABLED) {
                divisionMark = this.createArcDivisionMark(containerIndex, progress, isMajor, relativeBounds, housePosition, localConfig);
            } else {
                divisionMark = this.createStraightDivisionMark(containerIndex, progress, isMajor, relativeBounds, housePosition, localConfig);
            }

            if (divisionMark) {
                divisionMark.setAttribute('data-house', containerIndex.toString());
                divisionMark.setAttribute('data-degree', degree.toString());

                linesOverlay.node()?.appendChild(divisionMark);

                renderedElements.push({
                    id: `degree-division-house-${containerIndex}-deg-${degree}`,
                    element: divisionMark,
                    type: 'line',
                    layer: 'degrees-line',
                    debugInfo: {
                        renderTime: performance.now() - startTime,
                        elementCount: 1,
                        memoryEstimate: this.estimateElementMemory(divisionMark)
                    }
                });
            }
        }

        return renderedElements;
    }

    private createArcDivisionMark(containerIndex: number, progress: number, isMajor: boolean, relativeBounds: any, housePosition: any, localConfig: any): SVGElement {
        const arcPoint = this.getPointOnArcPath(containerIndex, progress, relativeBounds, 0.5);
        const tangentDirection = this.calculateArcTangent(containerIndex, progress, relativeBounds, 0.5);

        const perpX = -tangentDirection.y;
        const perpY = tangentDirection.x;

        const divisionLength = isMajor ? localConfig.MAJOR_DIVISION_LENGTH : localConfig.DIVISION_LENGTH;
        const halfLength = divisionLength / 2;

        const centerX = arcPoint.x + housePosition.x;
        const centerY = arcPoint.y + housePosition.y;

        const startX = centerX - perpX * halfLength;
        const startY = centerY - perpY * halfLength;
        const endX = centerX + perpX * halfLength;
        const endY = centerY + perpY * halfLength;

        const divisionMark = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        divisionMark.setAttribute('x1', startX.toString());
        divisionMark.setAttribute('y1', startY.toString());
        divisionMark.setAttribute('x2', endX.toString());
        divisionMark.setAttribute('y2', endY.toString());
        divisionMark.setAttribute('stroke', isMajor ? localConfig.MAJOR_DIVISION_COLOR : localConfig.STROKE_COLOR);
        divisionMark.setAttribute('stroke-width', localConfig.STROKE_WIDTH.toString());
        divisionMark.setAttribute('class', `degree-division arc-division ${isMajor ? 'major-division' : 'minor-division'}`);

        return divisionMark;
    }

    private createStraightDivisionMark(containerIndex: number, progress: number, isMajor: boolean, relativeBounds: any, housePosition: any, localConfig: any): SVGElement {
        const lineCoords = this.calculateDegreeLineCoordinatesOld(containerIndex, relativeBounds, 0.5);

        const lineStartX = lineCoords.startX + housePosition.x;
        const lineStartY = lineCoords.startY + housePosition.y;
        const lineEndX = lineCoords.endX + housePosition.x;
        const lineEndY = lineCoords.endY + housePosition.y;

        const lineDx = lineEndX - lineStartX;
        const lineDy = lineEndY - lineStartY;
        const lineLength = Math.sqrt(lineDx * lineDx + lineDy * lineDy);

        const perpX = -lineDy / lineLength;
        const perpY = lineDx / lineLength;

        const pointX = lineStartX + lineDx * progress;
        const pointY = lineStartY + lineDy * progress;

        const divisionLength = isMajor ? localConfig.MAJOR_DIVISION_LENGTH : localConfig.DIVISION_LENGTH;

        const halfOffset = divisionLength / 2;
        const mark1X = pointX + perpX * halfOffset;
        const mark1Y = pointY + perpY * halfOffset;
        const mark2X = pointX - perpX * halfOffset;
        const mark2Y = pointY - perpY * halfOffset;

        const divisionMark = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        divisionMark.setAttribute('x1', mark1X.toString());
        divisionMark.setAttribute('y1', mark1Y.toString());
        divisionMark.setAttribute('x2', mark2X.toString());
        divisionMark.setAttribute('y2', mark2Y.toString());
        divisionMark.setAttribute('stroke', isMajor ? localConfig.MAJOR_DIVISION_COLOR : localConfig.STROKE_COLOR);
        divisionMark.setAttribute('stroke-width', localConfig.STROKE_WIDTH.toString());
        divisionMark.setAttribute('class', `degree-division straight-division ${isMajor ? 'major-division' : 'minor-division'}`);

        return divisionMark;
    }

    // --- Arc path geometry ---

    private getPointOnArcPath(containerIndex: number, progress: number, containerBounds: any, insetRatio: number): { x: number, y: number } {
        const lineCoords = this.calculateDegreeLineCoordinatesOld(containerIndex, containerBounds, insetRatio);
        const pathData = this.createCornerArcPathOld(containerIndex, lineCoords, containerBounds, insetRatio);

        const tempPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        tempPath.setAttribute('d', pathData);

        const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        tempSvg.style.position = 'absolute';
        tempSvg.style.left = '-9999px';
        tempSvg.appendChild(tempPath);
        document.body.appendChild(tempSvg);

        try {
            const totalLength = tempPath.getTotalLength();
            const targetLength = totalLength * progress;
            const point = tempPath.getPointAtLength(targetLength);

            return { x: point.x, y: point.y };
        } finally {
            document.body.removeChild(tempSvg);
        }
    }

    private calculateConnectionLineRotation(
        containerIndex: number,
        totalDegrees: number,
        containerBounds: any,
        linePosition: { x: number, y: number },
        perpendicularPosition: { x: number, y: number }
    ): number {
        const deltaX = perpendicularPosition.x - linePosition.x;
        const deltaY = perpendicularPosition.y - linePosition.y;

        let angleDegrees = Math.atan2(deltaY, deltaX) * (180 / Math.PI);

        if (angleDegrees < 0) {
            angleDegrees += 360;
        }

        const finalRotation = angleDegrees - 90;

        return finalRotation;
    }

    private calculateArcTangent(containerIndex: number, progress: number, containerBounds: any, insetRatio: number): { x: number, y: number } {
        const epsilon = 0.001;
        const point1 = this.getPointOnArcPath(containerIndex, Math.max(0, progress - epsilon), containerBounds, insetRatio);
        const point2 = this.getPointOnArcPath(containerIndex, Math.min(1, progress + epsilon), containerBounds, insetRatio);

        const tangentX = point2.x - point1.x;
        const tangentY = point2.y - point1.y;

        const length = Math.sqrt(tangentX * tangentX + tangentY * tangentY);

        if (length === 0) {
            return { x: 1, y: 0 };
        }

        return {
            x: tangentX / length,
            y: tangentY / length
        };
    }

    // --- Planet position calculation ---

    private calculatePlanetPositionOld(containerIndex: number, planetDegree: number, containerBounds: any): { x: number, y: number } | null {
        const currentInsetRatio = 0.5;
        const lineCoords = this.calculateDegreeLineCoordinatesOld(containerIndex, containerBounds, currentInsetRatio);
        const progress = planetDegree / 30.0;

        const isCornerHouse = [0, 3, 6, 9].includes(containerIndex);

        let position: { x: number, y: number };
        if (isCornerHouse) {
            position = this.getPointOnArcPath(containerIndex, progress, containerBounds, currentInsetRatio);
        } else {
            const x = lineCoords.startX + (lineCoords.endX - lineCoords.startX) * progress;
            const y = lineCoords.startY + (lineCoords.endY - lineCoords.startY) * progress;
            position = { x, y };
        }

        return position;
    }

    // --- Corner arc path ---

    private createCornerArcPathOld(containerIndex: number, lineCoords: any, containerBounds: any, insetRatio: number = 0): string {
        const insetOffset = containerBounds.width * insetRatio;
        const minRadius = 10;
        const maxRadius = Math.min(containerBounds.width, containerBounds.height) * 0.6;
        const cornerRadius = maxRadius - (maxRadius - minRadius) * insetRatio;

        switch(containerIndex) {
            case 0:
                return `M ${lineCoords.startX} ${lineCoords.startY} L ${insetOffset} ${cornerRadius + insetOffset} A ${cornerRadius} ${cornerRadius} 0 0 1 ${cornerRadius + insetOffset} ${insetOffset} L ${lineCoords.endX} ${lineCoords.endY}`;
            case 3:
                return `M ${lineCoords.startX} ${lineCoords.startY} L ${containerBounds.width - cornerRadius - insetOffset} ${insetOffset} A ${cornerRadius} ${cornerRadius} 0 0 1 ${containerBounds.width - insetOffset} ${cornerRadius + insetOffset} L ${lineCoords.endX} ${lineCoords.endY}`;
            case 6:
                return `M ${lineCoords.startX} ${lineCoords.startY} L ${containerBounds.width - insetOffset} ${containerBounds.height - cornerRadius - insetOffset} A ${cornerRadius} ${cornerRadius} 0 0 1 ${containerBounds.width - cornerRadius - insetOffset} ${containerBounds.height - insetOffset} L ${lineCoords.endX} ${lineCoords.endY}`;
            case 9:
                return `M ${lineCoords.startX} ${lineCoords.startY} L ${cornerRadius + insetOffset} ${containerBounds.height - insetOffset} A ${cornerRadius} ${cornerRadius} 0 0 1 ${insetOffset} ${containerBounds.height - cornerRadius - insetOffset} L ${lineCoords.endX} ${lineCoords.endY}`;
            default:
                return `M ${lineCoords.startX} ${lineCoords.startY} A ${cornerRadius} ${cornerRadius} 0 0 1 ${lineCoords.endX} ${lineCoords.endY}`;
        }
    }

    // --- Secondary data rendering ---

    private handleSecondaryDataPositioning(event: any): void {
        const { data } = event;

        if (!this.spriteModule.getSecondaryDataSpriteManager() || !this.spriteModule.isSecondaryDataEnabled()) {
            devWarn('Secondary data sprite manager not available');
            return;
        }

        data.forEach((planetData: any, index: number) => {
            try {
                this.createSecondaryDataSpriteWithPositioning(planetData, index);
            } catch (_error) {
            }
        });
    }

    private createSecondaryDataSpriteWithPositioning(planetData: any, index: number): void {
        const planetName = planetData.graha;

        try {
            const preparationEngine = ElementPreparationEngine.getInstance();
            const preparedResult = preparationEngine.prepareElements({
                planets: [planetData]
            });

            if (preparedResult.elements.length === 0) {
                devWarn(`[SECONDARY DEBUG] Failed to prepare element for ${planetName}`);
                return;
            }

            const unifiedElement = preparedResult.elements[0];
            const containerIndex = unifiedElement.containerIndex;

            const positions = this.calculateSecondaryDataPositionFromUnifiedElement(unifiedElement, index);
            if (!positions) {
                return;
            }

            const rotation = this.calculateConnectionLineRotation(
                containerIndex,
                unifiedElement.degree,
                { width: 196, height: 196 },
                positions.linePosition,
                positions.externalPosition
            );

            const secondaryManager = this.spriteModule.getSecondaryDataSpriteManager()!;
            const sprite = secondaryManager.createSecondaryDataSprite(
                planetName,
                containerIndex,
                positions.externalPosition
            );

            if (sprite.connectionGroup) {
                sprite.connectionGroup.attr('transform', `rotate(${rotation})`);
            }

        } catch (error) {
            devError(`[SECONDARY DEBUG] Error creating sprite for ${planetName}:`, error);
        }
    }

    private calculateSecondaryDataPositionFromUnifiedElement(unifiedElement: any, positionIndex: number): {
        linePosition: { x: number, y: number };
        externalPosition: { x: number, y: number };
    } | null {
        const containerIndex = unifiedElement.containerIndex;
        const totalDegrees = unifiedElement.degree;

        const housePosition = this.calculateHousePositionForDegreeLines(containerIndex);
        const containerBounds = {
            width: housePosition.width,
            height: housePosition.height,
            x: 0,
            y: 0
        };

        const linePosition = this.calculatePlanetPositionOld(containerIndex, totalDegrees, containerBounds);
        if (!linePosition) {
            return null;
        }

        linePosition.x += housePosition.x;
        linePosition.y += housePosition.y;

        const localDegreeInHouse = totalDegrees % 30;
        const externalDistance = POSITION_CONSTANTS.BASE_DISTANCE + SECONDARY_DATA_SPRITE_CONFIG.EXTERNAL_OFFSET;
        const externalPosition = this.calculatePerpendicularPosition({
            containerIndex,
            containerBounds,
            linePosition,
            yogaDegree: localDegreeInHouse,
            positionIndex,
            isExternal: true,
            baseDistance: externalDistance
        });

        if (!externalPosition) {
            return null;
        }

        return {
            linePosition,
            externalPosition
        };
    }

    // --- Public secondary data methods ---

    public showSecondaryData(data: any[], label?: string): void {
        globalEventStream.emit(createEvent(
            'secondary.data.show',
            'element-rendering-engine',
            { data, visible: true, label }
        ));
    }

    public hideSecondaryData(label?: string): void {
        globalEventStream.emit(createEvent(
            'secondary.data.hide',
            'element-rendering-engine',
            { label }
        ));
    }

    public clearSecondaryData(): void {
        globalEventStream.emit(createEvent(
            'secondary.data.clear',
            'element-rendering-engine',
            {}
        ));
    }

    // --- Memory estimation ---

    private estimateElementMemory(element: SVGElement): number {
        let size = 100;
        size += element.attributes.length * 20;
        size += (element.textContent?.length || 0) * 2;
        return size;
    }

    private estimateMemoryUsage(elements: RenderedElement[]): number {
        return elements.reduce((total, element) => total + element.debugInfo.memoryEstimate, 0);
    }

    // --- Debug trace ---

    private generateDebugTrace(
        inputElements: PositionedElement[],
        renderedElements: RenderedElement[],
        renderTime: number
    ): string[] {
        return [
            `Input elements: ${inputElements.length}`,
            `Rendered elements: ${renderedElements.length}`,
            `Render time: ${renderTime.toFixed(2)}ms`,
            `Average time per element: ${(renderTime / renderedElements.length).toFixed(2)}ms`,
            `Memory usage: ${this.estimateMemoryUsage(renderedElements)} bytes`,
            `Layer distribution: ${renderedElements.map(e => e.layer).join(', ')}`
        ];
    }

    // --- Stats ---

    private updateRenderStats(duration: number, elementCount: number, memoryUsage: number): void {
        this.statsTracker.update(duration, (stats) => {
            stats.totalElements += elementCount;
            stats.memoryUsage = memoryUsage;
        });
    }

    public getRenderStats() {
        return this.statsTracker.get();
    }

    public clearLayer(layer: d3.Selection<SVGGElement, unknown, any, any>): void {
        if (layer.node()) {
            layer.selectAll('*').remove();
        }
    }

    public static setDebugTraces(enabled: boolean): void {
        DebugTraceManager.setAll(DEBUG_TRACES, enabled);
    }

    // --- Lifecycle ---

    public destroy(): void {
        this.eventHandler.destroy();
        this.globalPlanetCache.length = 0;
        this.spriteModule.deferredSpriteData.length = 0;
        this.spriteModule.dispose();
        devLog('ElementRenderingEngine: Memory cleanup completed');
    }

    public dispose(): void {
        try {
            this.spriteModule.dispose();
            this.eventHandler.destroy();
            ElementRenderingEngine.instance = null;
        } catch (error) {
            devError('ElementRenderingEngine: Error during disposal:', error);
        }
    }
}

// Export for browser console debugging
if (typeof window !== 'undefined') {
    window.ElementRenderingEngine = ElementRenderingEngine;

    // Add sprite debugging helper
    window.checkSprites = () => {
        const spriteGroups = document.querySelectorAll('.planet-sprites-group');
        const sprites = document.querySelectorAll('.planet-sprite');
        const circles = document.querySelectorAll('.planet-circle');
        const texts = document.querySelectorAll('.planet-text');
        const linesOverlay = document.querySelectorAll('.lines-overlay');

        return { linesOverlay, spriteGroups, sprites, circles, texts };
    };

    // Store latest chart data for secondary data system
    let latestChartData: any = null;

    globalEventStream.subscribe('chart.data.updated', (event: any) => {
        if (event && event.chartData && event.chartData.planets) {
            latestChartData = event.chartData;
        }
    });

    // Add secondary data debugging helper
    window.showSecondaryData = (data?: any[]) => {
        const engine = ElementRenderingEngine.getInstance();

        let testData = data;

        if (!testData) {
            if (latestChartData && latestChartData.planets && latestChartData.planets.length > 0) {
                testData = latestChartData.planets;
            } else {
                try {
                    const sharedDataContext = SharedDataContext.getInstance();
                    const chartData = sharedDataContext.get('chart.data');

                    if (chartData && chartData.planets && chartData.planets.length > 0) {
                        testData = chartData.planets;
                    } else {
                        const globalChartData = window.currentChartData ||
                                              window.sharedChartData ||
                                              (document as any).currentPlanetData;

                        if (globalChartData && globalChartData.planets && globalChartData.planets.length > 0) {
                            testData = globalChartData.planets;
                        } else {
                            testData = [
                                { graha: 'Sy', position: { degrees: 45, minutes: 30, seconds: 0 } },
                                { graha: 'Ch', position: { degrees: 120, minutes: 15, seconds: 0 } },
                                { graha: 'Ma', position: { degrees: 200, minutes: 0, seconds: 0 } }
                            ];
                        }
                    }
                } catch (_error) {
                    testData = [
                        { graha: 'Sy', position: { degrees: 45, minutes: 30, seconds: 0 } },
                        { graha: 'Ch', position: { degrees: 120, minutes: 15, seconds: 0 } },
                        { graha: 'Ma', position: { degrees: 200, minutes: 0, seconds: 0 } }
                    ];
                }
            }
        }

        if (testData) {
            engine.showSecondaryData(testData, 'test-secondary-data');
        } else {
            devWarn('[SECONDARY DEBUG] Failed to get data for display');
        }
    };

    window.hideSecondaryData = () => {
        const engine = ElementRenderingEngine.getInstance();
        engine.hideSecondaryData();
    };

    window.checkChartData = () => {
        try {
            const sharedDataContext = SharedDataContext.getInstance();
            const chartData = sharedDataContext.get('chart.data');
            return chartData ?? window.currentChartData ?? window.sharedChartData;
        } catch {
            return window.currentChartData ?? window.sharedChartData;
        }
    };

    window.checkSecondaryDataDOMPosition = () => {
        const svg = document.querySelector('svg.astrology-chart-svg');
        if (!svg) {
            return;
        }

        const mainViewport = svg.querySelector('.viewport-content.main-viewport');
        if (!mainViewport) {
            return;
        }

        if (mainViewport) {
            return Array.from(mainViewport.children).map(child => {
                if (child instanceof SVGElement && child.className && typeof child.className === 'object' && 'baseVal' in child.className) {
                    return (child.className as any).baseVal || 'no-class';
                } else if (child.classList) {
                    return child.classList.toString() || 'no-class';
                }
                return 'no-class';
            });
        }
        return [];
    };

    window.testDuplicateBugFix = () => {
        const engine = ElementRenderingEngine.getInstance();
        const testData = [
            { graha: 'Sy', longitude: 45.5, rasi: 2 },
            { graha: 'Ch', longitude: 120.3, rasi: 5 },
            { graha: 'Ma', longitude: 200.1, rasi: 7 }
        ];

        engine.hideSecondaryData();

        setTimeout(() => {
            engine.showSecondaryData(testData, 'single-call-test');

            setTimeout(() => {
                const spriteGroup = document.querySelector('.secondary-data-sprites-group');
                if (spriteGroup) {
                    const sprites = spriteGroup.querySelectorAll('.secondary-data-sprite');

                    if (sprites.length === 3) {
                        engine.showSecondaryData(testData, 'multi-call-test-1');
                        engine.showSecondaryData(testData, 'multi-call-test-2');
                        engine.showSecondaryData(testData, 'multi-call-test-3');
                    }
                }
            }, 300);
        }, 200);
    };
}
